import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { CookieService } from 'ngx-cookie';
import { IsPeriodNavigatorService } from './is-period-navigator.service';

@Component({
  selector: 'is-period-navigator',
  templateUrl: './is-period-navigator.component.html',
  styleUrls: ['./is-period-navigator.component.scss']
})
export class IsPeriodNavigatorComponent implements OnInit {

  // Form control for date
  @Input() dateControl: UntypedFormControl = new UntypedFormControl(new Date());
  // Form control for frequency
  @Input() frequencyControl = new UntypedFormControl('daily');
  @Input() aggregationControl = new UntypedFormControl();

  // TO DO: activate and disactivate yearly from frequencies list
  @Input() yearly: boolean = true;

  @Input() aggregationPlaceholder = this._translateService.instant('real_time');
  // Form group for range date
  @Input() rangeControl = new UntypedFormGroup({
    start: new UntypedFormControl(),
    end: new UntypedFormControl()
  });
  // Min for minimum date and max for maximum date
  @Input() min;
  @Input() max;
  @Input() isFrequency: boolean = true;
  @Input() isAggregation: boolean = false;
  @Input() choiceSet: boolean = true;
  @Input() layout: string = 'righty';
  @Input() justify: string = 'flex-end';
  @Input() frequencyAppearance: string = 'legacy';
  // List of frequencies
  public frequenciesList: any[] = ['daily', 'weekly', 'monthly', 'yearly', 'personalized'];
  public aggregationList: any[] = [];

  @Input() isDarkTheme: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _translateService: TranslateService,
    public _datePicker: IsPeriodNavigatorService,
    private _cookie: CookieService
  ) {
  }

  ngOnInit(): void {
    if (!this.yearly) {
      const index = this.frequenciesList.indexOf('yearly');
      this.frequenciesList.splice(index, 1);
    }
    if (this.isFrequency) {
      this.initializeDateByCookies();
      if (this.isAggregation) {
        this.generateAggregationList();
      }
    } else {
      if (this.choiceSet) {
        this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value);
      } else {
        this._datePicker.setRangeParsedDate(this.rangeControl.value);
      }
    }
  }

  private initializeDateByCookies(): void {
    const timeFrom = this._cookie.get('time_from');
    const timeTo = this._cookie.get('time_to');
    const period = this._cookie.get('period');

    if (timeFrom && period !== 'personalized') {
      this.choiceSet = true;
      this.dateControl.setValue(new Date(timeFrom));
      this.frequencyControl.setValue(period);
      this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value);
    } else if (timeFrom && period === 'personalized') {
      this.choiceSet = false;
      this.rangeControl.setValue({
        start: new Date(timeFrom),
        end: new Date(timeTo)
      });
      this.frequencyControl.setValue(period);
      this.dateControl.setValue(new Date(timeFrom));
      this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value);
    }
  }

  // Called when an element used to change the date is clicked
  public changeDate(event): void {

    let newDate;
    // Branch entered if the element calling the function is a button that adds a numeric value to shuffle through the dates
    if (typeof event === 'number' || typeof event === 'string') {
      if (this.frequencyControl?.value === 'daily') {
        this.choiceSet = true;
        // if I have to add or subtract days i parse the
        // day using moment and reassign it as date
        newDate = new Date(moment(this.dateControl?.value).add(event, 'day').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);

      } else if (this.frequencyControl?.value === 'weekly') {
        this.choiceSet = true;
        // if I have to add or subtract month i parse the
        // day using moment and reassign it as date
        newDate = new Date(moment(this.dateControl?.value).add(event, 'week').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);

      } else if (this.frequencyControl?.value === 'monthly') {
        this.choiceSet = true;
        // if I have to add or subtract month i parse the
        // day using moment and reassign it as date

        newDate = new Date(moment(this.dateControl?.value).add(event, 'month').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);

      } else if (this.frequencyControl?.value === 'yearly') {
        this.choiceSet = true;
        // if I have to add or subtract year i parse the
        // day using moment and reassign it as date

        newDate = new Date(moment(this.dateControl?.value).add(event, 'year').toISOString());
        newDate.setHours(0, 0, 0, 0);
        this.dateControl.setValue(newDate);

      } else if (this.frequencyControl?.value === 'personalized') {
        // if I have to add or subtract year i parse the
        // day using moment and reassign it as date
        this.choiceSet = false;
        const start = this.dateControl?.value;
        const end = new Date(moment(this.dateControl?.value).add(1, 'day').toISOString());
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        this.rangeControl.setValue({
          start: start,
          end: end
        });
      }
    } else if (typeof event.value === 'object') {
      this.dateControl.setValue(new Date(event.value));
    } // Branch entered if the date is picked from the calendar dialog
    // Calls method to set the selected date
    this._datePicker.setDate(this.dateControl.value, this.frequencyControl.value, this.aggregationControl.value);
  }

  public changeFrequency(event): void {
    if (this.isAggregation) {
      this.generateAggregationList();
    }
    this.aggregationControl.reset();
    this.changeDate(event);
  }

  public changeAggregation(event): void {
    this.changeDate(event);
  }


  // Deactivates the buttons if max/min are set and the date range limit is reached
  public canChangeWith(param: number): boolean {
    const dateWhereMove = new Date(
      moment(this.dateControl?.value).add(param, 'days').toISOString()
    );
    const max = new Date(this.max);
    const min = new Date(this.min);
    if (this.max && max && dateWhereMove > max) {
      return false;
    }
    if (this.min && min && dateWhereMove < min) {
      return false;
    }
    return true;
  }

  // Returns current selected date formatted depending on the frequency
  public getCurrentDate(): string {
    let c: string;
    if (this.frequencyControl.value === 'daily') {
      c = moment(this.dateControl.value).format('DD MMM YYYY');
    } else if (this.frequencyControl.value === 'monthly') {
      c = moment(this.dateControl.value).format('MMMM YYYY');
    } else if (this.frequencyControl.value === 'weekly') {
      c = moment(this.dateControl.value).format('DD MMM YYYY');
      c += ' - ' + moment(this.dateControl.value).add(1, 'week').subtract(1, 'ms').format('DD MMM YYYY');
    } else if (this.frequencyControl.value === 'yearly') {
      c = moment(this.dateControl.value).format('YYYY');
    } else if (this.frequencyControl.value === 'personalized') {
      c = moment(this.rangeControl.value.start).format('DD MMM YYYY') + ' - ' + moment(this.rangeControl.value.end).format('DD MMM YYYY');
    }
    return c;
  }

  public setDate(event): void {
    this._datePicker.setRangeParsedDate(this.rangeControl.value);
  }

  public frequencyView(): string {
    switch (this.frequencyControl.value) {
      case 'yearly':
        return 'multi-year';
      case 'monthly':
        return 'year';
      default:
        return 'month';
    }
  }

  public generateAggregationList(): void {
    this.aggregationList = [];
    this.aggregationControl.reset();
    switch (this.frequencyControl.value) {
      case 'daily':
        this.aggregationList.push('minutes');
        this.aggregationList.push('hours');
        break;
      case 'weekly':
        this.aggregationList.push('minutes');
        this.aggregationList.push('hours');
        this.aggregationList.push('daily');
        break;
      case 'monthly':
        this.aggregationList.push('hours');
        this.aggregationList.push('daily');
        this.aggregationList.push('weeks');
        break;
      case 'yearly':
        this.aggregationList.push('hours');
        this.aggregationList.push('daily');
        this.aggregationList.push('weeks');
        this.aggregationList.push('monthly');
        break;
      case 'personalized':
        this.aggregationList.push('hours');
        this.aggregationList.push('daily');
        this.aggregationList.push('weeks');
        this.aggregationList.push('monthly');
        break;
    }
  }

}
